import { defineComponent as _defineComponent } from 'vue'
import { twMerge } from 'tailwind-merge'
import { ref, onMounted, computed } from 'vue'
import { useDropZone } from '@vueuse/core'

export interface DropZoneProps {
  /**
   * The supported file types for the drop zone.
   *
   * @default ['*'] All file types are supported.
   * @example ['image/*', 'application/pdf']
   */
  supportedFileTypes?: string[]
  /**
   * Allow multiple files to be dropped.
   *
   * @default true
   */
  multiple?: boolean
  /**
   * Highlight the drop zone when dragging files over it.
   * @default true
   */
  highlightOnDropZone?: boolean
  /**
   * Highlight the drop zone when dragging files over the window.
   * If the highlightOnDropZone prop is set to false, this prop will be ignored.
   *
   * @default false
   */
  highlightOnWindowDrag?: boolean
  /**
   * Allow to drop files everywhere on the page, not just the designated drop zone area.
   *
   * @default false
   */
  allowDropEverywhere?: boolean
  /**
   * The class names to apply to the drop z one.
   *
   * @default undefined
   */
  twClass?: string | string[]
  /**
   * A hint to show when the file type is unsupported. Giving the user a hint
   * which file types are supported.
   */
  unsupportedFileTypeHint?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DropZone',
  props: {
    supportedFileTypes: { default: () => ['*'] },
    multiple: { type: Boolean, default: true },
    highlightOnDropZone: { type: Boolean, default: true },
    highlightOnWindowDrag: { type: Boolean, default: false },
    allowDropEverywhere: { type: Boolean, default: false },
    twClass: { default: undefined },
    unsupportedFileTypeHint: { default: undefined }
  },
  emits: ["drop"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'drop', files: File[] | null, event: DragEvent): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { supportedFileTypes: string[], multiple: boolean, highlightOnDropZone: boolean, highlightOnWindowDrag: boolean, allowDropEverywhere: boolean, twClass: string | string[], unsupportedFileTypeHint: string };





const dropZoneRef = ref<HTMLDivElement | null>(null)
const windowDropZoneRef = ref<HTMLElement | null>(null)

onMounted(() => {
  windowDropZoneRef.value = document.body
})

const isValidType = ref(false)

const highlightDropZone = computed(() => {
  if (!props.highlightOnDropZone) {
    return false
  }

  return props.highlightOnWindowDrag
    ? isOverWindowDropZone.value
    : isOverDropZone.value
})

const { isOverDropZone: isOverWindowDropZone } = useDropZone(
  windowDropZoneRef,
  {
    onEnter: (_files, event) => {
      if (event.dataTransfer?.items) {
        isValidType.value = checkValidity(event.dataTransfer.items)
      }
    },
    multiple: props.multiple,
    onDrop: props.allowDropEverywhere ? onDrop : undefined,
  },
)

const { isOverDropZone } = useDropZone(dropZoneRef, {
  multiple: props.multiple,
  onDrop,
})

function onDrop(files: File[] | null, event: DragEvent) {
  event.preventDefault()

  if (isValidType.value && files) {
    emit('drop', files, event)
  }
}

// Helper functions to check if the file types are valid -----------------------

function checkValidity(items: DataTransferItemList) {
  const types = Array.from(items ?? []).map((item) => item.type)

  const dataTypesValid = checkDataTypes(types)
  const multipleFilesValid = props.multiple || items.length <= 1

  return dataTypesValid && multipleFilesValid
}

function checkDataTypes(types: readonly string[]) {
  const dataTypes = props.supportedFileTypes

  if (!dataTypes?.length) {
    return true
  } else if (types.length === 0) {
    return false
  }

  return types.every((type) =>
    dataTypes.some((allowedType) => type.includes(allowedType)),
  )
}

return { __sfc: true,props, emit, dropZoneRef, windowDropZoneRef, isValidType, highlightDropZone, isOverWindowDropZone, isOverDropZone, onDrop, checkValidity, checkDataTypes, twMerge }
}

})